(function () {
  class SetaraScentSlider {
    constructor(root) {
      this.root = root;
      this.viewport = root.querySelector('[data-scent-viewport]');
      this.track = root.querySelector('[data-scent-track]');
      this.slides = Array.from(root.querySelectorAll('[data-scent-slide]'));
      this.prevBtn = root.querySelector('[data-scent-prev]');
      this.nextBtn = root.querySelector('[data-scent-next]');

      if (!this.viewport || !this.track || this.slides.length === 0) return;

      this.index = 0;
      this.speed = (parseInt(root.dataset.speed, 10) || 4) * 1000;
      this.autoplay = root.dataset.autoplay === 'true' && this.slides.length > 1;
      this.timer = null;
      this.columns = 1;
      this.maxIndex = 0;
      this.stepPx = 0;
      this.touchStartX = 0;
      this.touchBaseOffset = 0;
      this.isTouching = false;
      this.isMobile = false;

      this.onPrev = () => this.go(-1, true);
      this.onNext = () => this.go(1, true);
      this.onResize = () => this.measure();
      this.onMouseEnter = () => this.pause();
      this.onMouseLeave = () => {
        if (this.autoplay) this.play();
      };
      this.onFocusIn = () => this.pause();
      this.onFocusOut = (event) => {
        if (!this.root.contains(event.relatedTarget) && this.autoplay) this.play();
      };
      this.onTouchStart = (event) => this.handleTouchStart(event);
      this.onTouchMove = (event) => this.handleTouchMove(event);
      this.onTouchEnd = (event) => this.handleTouchEnd(event);

      if (this.prevBtn) this.prevBtn.addEventListener('click', this.onPrev);
      if (this.nextBtn) this.nextBtn.addEventListener('click', this.onNext);
      this.root.addEventListener('mouseenter', this.onMouseEnter);
      this.root.addEventListener('mouseleave', this.onMouseLeave);
      this.root.addEventListener('focusin', this.onFocusIn);
      this.root.addEventListener('focusout', this.onFocusOut);
      this.viewport.addEventListener('touchstart', this.onTouchStart, { passive: true });
      this.viewport.addEventListener('touchmove', this.onTouchMove, { passive: false });
      this.viewport.addEventListener('touchend', this.onTouchEnd, { passive: true });
      this.viewport.addEventListener('touchcancel', this.onTouchEnd, { passive: true });
      window.addEventListener('resize', this.onResize);

      this.measure();
      this.show(this.index, false);
      if (this.autoplay && !window.matchMedia('(prefers-reduced-motion: reduce)').matches) this.play();
    }

    destroy() {
      this.pause();
      if (this.prevBtn) this.prevBtn.removeEventListener('click', this.onPrev);
      if (this.nextBtn) this.nextBtn.removeEventListener('click', this.onNext);
      this.root.removeEventListener('mouseenter', this.onMouseEnter);
      this.root.removeEventListener('mouseleave', this.onMouseLeave);
      this.root.removeEventListener('focusin', this.onFocusIn);
      this.root.removeEventListener('focusout', this.onFocusOut);
      this.viewport.removeEventListener('touchstart', this.onTouchStart);
      this.viewport.removeEventListener('touchmove', this.onTouchMove);
      this.viewport.removeEventListener('touchend', this.onTouchEnd);
      this.viewport.removeEventListener('touchcancel', this.onTouchEnd);
      window.removeEventListener('resize', this.onResize);
    }

    getColumns() {
      const mobile = window.matchMedia('(max-width: 749px)').matches;
      const raw = mobile ? this.root.dataset.columnsMobile : this.root.dataset.columnsDesktop;
      const columns = parseInt(raw, 10) || (mobile ? 3 : 5);
      return Math.min(Math.max(columns, 1), this.slides.length);
    }

    measure() {
      this.isMobile = window.matchMedia('(max-width: 749px)').matches;
      this.columns = this.getColumns();
      this.maxIndex = Math.max(0, this.slides.length - this.columns);
      if (this.index > this.maxIndex) this.index = 0;

      const gap = parseFloat(getComputedStyle(this.root).getPropertyValue('--setara-scent-gap')) * 16 || 16;
      const viewportWidth = this.viewport.clientWidth;
      const slideWidth = (viewportWidth - gap * (this.columns - 1)) / this.columns;

      this.root.style.setProperty('--setara-scent-slide-width', slideWidth + 'px');
      this.stepPx = slideWidth + gap;

      this.show(this.index, false);
      this.updateButtons();
    }

    show(index, animate) {
      this.index = Math.min(Math.max(index, 0), this.maxIndex);
      this.track.classList.toggle('setara-scent-slider__track--animate', animate);
      this.track.classList.toggle('setara-scent-slider__track--animate-mobile', animate && this.isMobile);
      this.track.style.transform = 'translate3d(-' + this.index * this.stepPx + 'px, 0, 0)';
      this.updateButtons();
    }

    go(delta, animate) {
      let next = this.index + delta;
      if (next > this.maxIndex) next = 0;
      if (next < 0) next = this.maxIndex;
      this.show(next, animate);
      if (this.autoplay) this.play();
    }

    next() {
      this.go(1, true);
    }

    play() {
      this.pause();
      this.timer = window.setTimeout(() => this.next(), this.speed);
    }

    pause() {
      if (this.timer) {
        window.clearTimeout(this.timer);
        this.timer = null;
      }
    }

    handleTouchStart(event) {
      if (!this.isMobile || event.touches.length !== 1) return;

      this.pause();
      this.isTouching = true;
      this.touchStartX = event.touches[0].clientX;
      this.touchBaseOffset = -this.index * this.stepPx;
      this.track.classList.remove('setara-scent-slider__track--animate');
      this.track.classList.remove('setara-scent-slider__track--animate-mobile');
    }

    handleTouchMove(event) {
      if (!this.isTouching || event.touches.length !== 1) return;

      const deltaX = event.touches[0].clientX - this.touchStartX;

      if (Math.abs(deltaX) > 8) {
        event.preventDefault();
      }

      let offset = this.touchBaseOffset + deltaX;
      const minOffset = -this.maxIndex * this.stepPx;
      const maxOffset = 0;

      if (offset > maxOffset) {
        offset = maxOffset + (offset - maxOffset) * 0.35;
      } else if (offset < minOffset) {
        offset = minOffset + (offset - minOffset) * 0.35;
      }

      this.track.style.transform = 'translate3d(' + offset + 'px, 0, 0)';
    }

    handleTouchEnd(event) {
      if (!this.isTouching) return;

      this.isTouching = false;
      const deltaX = this.touchStartX - event.changedTouches[0].clientX;
      const threshold = Math.min(50, this.stepPx * 0.2);

      if (Math.abs(deltaX) > threshold) {
        this.go(deltaX > 0 ? 1 : -1, true);
      } else {
        this.show(this.index, true);
      }

      if (this.autoplay && !window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
        this.play();
      }
    }

    updateButtons() {
      const singlePage = this.maxIndex === 0;
      if (this.prevBtn) this.prevBtn.disabled = singlePage;
      if (this.nextBtn) this.nextBtn.disabled = singlePage;
    }
  }

  const instances = new WeakMap();

  function boot(scope) {
    scope.querySelectorAll('[data-scent-slider]').forEach((root) => {
      const prev = instances.get(root);
      if (prev) {
        prev.destroy();
        instances.delete(root);
      }

      const slider = new SetaraScentSlider(root);
      if (slider.track) instances.set(root, slider);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => boot(document));
  } else {
    boot(document);
  }

  window.addEventListener('resize', () => {
    instances.forEach((slider) => slider.measure && slider.measure());
  });

  document.addEventListener('shopify:section:load', (event) => boot(event.target));
  document.addEventListener('shopify:section:unload', (event) => {
    event.target.querySelectorAll('[data-scent-slider]').forEach((root) => {
      const slider = instances.get(root);
      if (slider) {
        slider.destroy();
        instances.delete(root);
      }
    });
  });
})();
